import { zodResolver } from '@hookform/resolvers/zod';
import React, { createContext, useContext } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import {
  CalculateMaximumMortgageFormSchema,
  type CalculateMaximumMortgageFormValues,
} from '~/pages/Tools/CalculateMaximumMortgage/Calculate/schema';
import {
  MortgagePeriod,
  MortgagePeriodMap,
  MortgageType,
} from '~/types/Calculations';

const CalculateMaximumMortgageFormContext = createContext<
  ReturnType<typeof useForm<CalculateMaximumMortgageFormValues>> | undefined
>(undefined);

type CalculateMaximumMortgageFormProviderProps = {
  children: React.ReactNode;
  defaultIncome?: number;
  defaultAge?: number;
  defaultType: MortgageType;
  defaultPeriod: MortgagePeriod;
};

export const CalculateMaximumMortgageFormProvider: React.FC<
  CalculateMaximumMortgageFormProviderProps
> = ({ children, defaultIncome, defaultAge, defaultType, defaultPeriod }) => {
  const form = useForm<CalculateMaximumMortgageFormValues>({
    resolver: zodResolver(CalculateMaximumMortgageFormSchema),
    defaultValues: {
      income: defaultIncome || undefined,
      age: defaultAge || undefined,
      hasPartner: false,
      partnerIncome: undefined,
      partnerAge: undefined,
      hasObligations: false,
      loans: undefined,
      alimony: undefined,
      studentDebt: undefined,
      mortgageType: defaultType,
      interestPeriod: MortgagePeriodMap[defaultPeriod],
      useNhg: false,
      energyLabel: 'Unknown',
    },
    mode: 'onSubmit',
    reValidateMode: 'onChange',
    shouldUnregister: false,
  });

  return (
    <CalculateMaximumMortgageFormContext.Provider value={form}>
      <FormProvider {...form}>{children}</FormProvider>
    </CalculateMaximumMortgageFormContext.Provider>
  );
};

export const useCalculateMaximumMortgageForm = () => {
  const context = useContext(CalculateMaximumMortgageFormContext);
  if (!context) {
    throw new Error(
      'useCalculateMaximumMortgageForm must be used within a CalculateMaximumMortgageFormProvider'
    );
  }
  return context;
};
